/**
 * GroupManager — field-group management surface (PIM Wave P1 Task 4, design
 * notes Surface 2). A self-contained content component (no `open`/`onClose`
 * of its own) so it can be mounted in either of its two call sites:
 *
 *  (a) `FieldLibraryPage`'s toolbar "Manage groups" button opens it inside a
 *      `BottomSheet`.
 *  (b) `FieldFormDrawer`'s "manage groups" affordance renders it INLINE
 *      (not in a second `BottomSheet`) — see the stacking note below.
 *
 * BottomSheet-over-BottomSheet stacking verification (brief §7): read
 * `packages/ui/src/BottomSheet.tsx` — the panel is a `motion.div` animated
 * via Framer Motion `x`/`y` motion values (`initial`/`animate` include
 * `x: 0, y: 0` even at rest), which Framer Motion renders as an inline
 * `transform: translateX(0px) translateY(0px)` — NOT `transform: none`. Any
 * CSS `transform` other than `none` creates a new containing block for
 * `position: fixed` descendants (CSS Transforms spec), so a `BottomSheet`
 * nested as `children` of ANOTHER open `BottomSheet` would have its own
 * `fixed inset-0` scrim/panel positioned against the OUTER panel's box, not
 * the viewport — broken, not just cosmetically. Grepped the rest of the
 * codebase for a JSX-nested `<BottomSheet>` (one literally inside another's
 * `children`): none exists — every existing multi-sheet surface (e.g.
 * `ProductDrawer.tsx:143-254`) renders its confirm sheet as a SIBLING, never
 * nested. That absence plus the transform math is enough to call it: nested
 * stacking is NOT viable here.
 *
 * Consequence: because GroupManager is ALWAYS mounted inside some open
 * `BottomSheet`'s panel in BOTH call sites above (the toolbar's own sheet,
 * or the drawer's), its own delete confirmation cannot use a second
 * `BottomSheet` in EITHER placement — so delete uses an INLINE row-level
 * confirm (the row's action button swaps for "confirm/cancel" text in
 * place) instead of the brief's literal "confirm via BottomSheet" wording.
 * This generalizes the brief's own pre-authorized fallback for item 7 to
 * item 6's delete action, for the same underlying reason.
 *
 * Backend semantics verified (brief §"Backend facts"): `DELETE
 * /admin/catalog/field-groups/{id}` is UNCONDITIONAL — group delete never
 * blocks on a nonempty group. `attribute_fields.group_id` is
 * `ON DELETE SET NULL` (`catalog_schema/models.py:108`) and
 * `repository.delete_group` docstring confirms: "groups own nothing ...
 * deleting a group never orphans a dependent row — it just un-groups its
 * fields" (`repository.py:295-303`; `service.delete_group`,
 * `service.py:1083-1110`, wraps it in one transaction + an audit row, no
 * pre-delete emptiness check anywhere). The confirm copy states this
 * plainly: the group is removed immediately and its fields become
 * ungrouped, not deleted.
 */
import { useEffect, useMemo, useRef, useState, type ReactElement } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { GripVertical, Trash2 } from "lucide-react";

import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

import { Button, Field } from "@garden/ui";
import { TrilingualInput, type TrilingualValue } from "@/features/admin/_shared";
import { ADMIN_NS, registerAdminI18n } from "@/features/admin/api";
import { ApiError } from "@/lib/api-client";
import type { AttributeFieldGroup } from "@/features/admin/attribute-fields/api";
import {
  useAttributeFieldGroups,
  useCreateAttributeFieldGroup,
  useDeleteAttributeFieldGroup,
  useUpdateAttributeFieldGroup,
} from "@/features/admin/attribute-fields/hooks";
import {
  FIELD_SLUG_MAX_LEN,
  FIELD_SLUG_PATTERN,
  slugify,
} from "@/features/admin/attribute-fields/lib/slug";

const EMPTY_GROUPS: readonly AttributeFieldGroup[] = [];

function toTrilingual(group: AttributeFieldGroup): TrilingualValue {
  return { ru: group.name_ru, ky: group.name_ky ?? "", en: group.name_en ?? "" };
}

function sameTrilingual(draft: TrilingualValue, group: AttributeFieldGroup): boolean {
  return (
    draft.ru === group.name_ru &&
    draft.ky === (group.name_ky ?? "") &&
    draft.en === (group.name_en ?? "")
  );
}

// ---------------------------------------------------------------------
// One sortable group row
// ---------------------------------------------------------------------

interface GroupRowProps {
  group: AttributeFieldGroup;
  index: number;
  dragDisabled: boolean;
}

function GroupRow({ group, index, dragDisabled }: GroupRowProps): ReactElement {
  const { t } = useTranslation(ADMIN_NS);
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: group.id,
    disabled: dragDisabled,
  });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition: transition ?? undefined,
    opacity: isDragging ? 0.6 : 1,
  };

  const [draft, setDraft] = useState<TrilingualValue>(() => toTrilingual(group));
  useEffect(() => {
    setDraft(toTrilingual(group));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [group.name_ru, group.name_ky, group.name_en]);

  const isDirty = !sameTrilingual(draft, group);
  const updateMutation = useUpdateAttributeFieldGroup();
  const deleteMutation = useDeleteAttributeFieldGroup();
  const [confirmingDelete, setConfirmingDelete] = useState(false);

  const save = (): void => {
    updateMutation.mutate(
      {
        id: group.id,
        body: {
          name_ru: draft.ru.trim(),
          name_ky: draft.ky.trim() || null,
          name_en: draft.en.trim() || null,
        },
      },
      { onError: () => toast.error(t("error.generic")) },
    );
  };

  const confirmDelete = (): void => {
    deleteMutation.mutate(group.id, {
      onSuccess: () => toast.success(t("pim.groups.delete.success")),
      onError: () => {
        toast.error(t("error.generic"));
        setConfirmingDelete(false);
      },
    });
  };

  return (
    <li
      ref={setNodeRef}
      style={style}
      data-testid="pim-group-row"
      className="flex flex-col gap-2 rounded-[var(--g-radius-sm)] border border-[var(--g-hairline)] bg-[var(--g-surface)] p-3 motion-reduce:!transition-none"
    >
      <div className="flex items-start gap-2">
        <button
          type="button"
          {...attributes}
          {...listeners}
          disabled={dragDisabled}
          aria-label={t("pim.groups.dragHandle", { index: index + 1 })}
          data-testid={`pim-group-drag-${group.id}`}
          className="mt-2 inline-flex size-7 shrink-0 cursor-grab items-center justify-center rounded-[var(--g-radius-sm)] text-[var(--g-secondary)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--g-primary)] disabled:cursor-not-allowed disabled:opacity-40"
        >
          <GripVertical size={16} strokeWidth={1.75} aria-hidden />
        </button>
        <span className="mt-2.5 shrink-0 whitespace-nowrap font-mono text-xs text-[var(--g-secondary)]">
          {group.slug}
        </span>
        <div className="flex-1">
          <TrilingualInput
            id={`pim-group-${group.id}`}
            label={t("pim.groups.rowLabel")}
            value={draft}
            onChange={setDraft}
            compact
          />
        </div>
      </div>

      <div className="flex items-center justify-end gap-2">
        {isDirty ? (
          <Button
            type="button"
            variant="primary"
            size="sm"
            loading={updateMutation.isPending}
            onClick={save}
            data-testid={`pim-group-save-${group.id}`}
            className="min-h-[44px]"
          >
            {t("common.save")}
          </Button>
        ) : null}

        {confirmingDelete ? (
          <>
            <span role="alert" className="text-xs text-[var(--g-error)]">
              {t("pim.groups.delete.confirmBody")}
            </span>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              disabled={deleteMutation.isPending}
              onClick={() => setConfirmingDelete(false)}
              className="min-h-[44px]"
            >
              {t("common.cancel")}
            </Button>
            <Button
              type="button"
              variant="destructive"
              size="sm"
              loading={deleteMutation.isPending}
              onClick={confirmDelete}
              data-testid={`pim-group-delete-confirm-${group.id}`}
              className="min-h-[44px]"
            >
              {t("pim.groups.delete.confirm")}
            </Button>
          </>
        ) : (
          <button
            type="button"
            data-testid={`pim-group-delete-${group.id}`}
            aria-label={t("pim.groups.delete.action")}
            onClick={() => setConfirmingDelete(true)}
            className="grid h-9 w-9 place-items-center rounded-full text-[var(--g-secondary)] hover:bg-[var(--g-elevated)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--g-primary)]"
          >
            <Trash2 size={16} strokeWidth={1.75} aria-hidden />
          </button>
        )}
      </div>
    </li>
  );
}

// ---------------------------------------------------------------------
// Add-group row
// ---------------------------------------------------------------------

interface AddGroupRowProps {
  nextSortOrder: number;
}

function AddGroupRow({ nextSortOrder }: AddGroupRowProps): ReactElement {
  const { t } = useTranslation(ADMIN_NS);
  const [nameRu, setNameRu] = useState("");
  const [slug, setSlug] = useState("");
  const [slugTouched, setSlugTouched] = useState(false);
  const [slugError, setSlugError] = useState<string | null>(null);
  const ruInputRef = useRef<HTMLInputElement | null>(null);
  const createMutation = useCreateAttributeFieldGroup();

  useEffect(() => {
    if (!slugTouched) setSlug(slugify(nameRu));
  }, [nameRu, slugTouched]);

  const trimmedRu = nameRu.trim();
  const slugValid = FIELD_SLUG_PATTERN.test(slug);
  const canSubmit = trimmedRu !== "" && slugValid && !createMutation.isPending;

  const submit = (): void => {
    if (!canSubmit) return;
    setSlugError(null);
    createMutation.mutate(
      { slug, name_ru: trimmedRu, sort_order: nextSortOrder },
      {
        onSuccess: () => {
          setNameRu("");
          setSlug("");
          setSlugTouched(false);
          ruInputRef.current?.focus();
        },
        onError: (err) => {
          if (err instanceof ApiError && err.code === "catalog_schema.field_group.duplicate_slug") {
            setSlugError(t("pim.groups.add.slugDuplicate"));
            return;
          }
          toast.error(t("error.generic"));
        },
      },
    );
  };

  return (
    <div
      className="flex flex-col gap-2 rounded-[var(--g-radius-sm)] border border-dashed border-[var(--g-hairline)] p-3"
      data-testid="pim-group-add-row"
    >
      <div className="flex flex-col gap-2 sm:flex-row sm:items-end">
        <div className="flex-1">
          <Field
            ref={ruInputRef}
            label={t("pim.groups.add.ruLabel")}
            data-testid="pim-group-add-ru"
            value={nameRu}
            onChange={(event) => setNameRu(event.target.value)}
          />
        </div>
        <div className="flex-1">
          <Field
            label={t("pim.groups.add.slugLabel")}
            data-testid="pim-group-add-slug"
            hint={slugError ? undefined : t("pim.groups.add.slugHint")}
            error={slugError ?? (slug !== "" && !slugValid ? t("pim.fields.form.formError.slugPattern") : undefined)}
            value={slug}
            maxLength={FIELD_SLUG_MAX_LEN}
            onChange={(event) => {
              setSlugTouched(true);
              setSlugError(null);
              setSlug(event.target.value);
            }}
          />
        </div>
        <Button
          type="button"
          variant="secondary"
          size="sm"
          leading="plus"
          loading={createMutation.isPending}
          disabled={!canSubmit}
          onClick={submit}
          data-testid="pim-group-add-submit"
          className="min-h-[44px]"
        >
          {t("pim.groups.add.submit")}
        </Button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------
// GroupManager shell
// ---------------------------------------------------------------------

export function GroupManager(): ReactElement {
  registerAdminI18n();
  const { t } = useTranslation(ADMIN_NS);
  const { data, isLoading, isError, refetch } = useAttributeFieldGroups();
  const groups = useMemo(
    () => (data?.groups ?? EMPTY_GROUPS).slice().sort((a, b) => a.sort_order - b.sort_order),
    [data],
  );

  const [optimisticIds, setOptimisticIds] = useState<string[] | null>(null);
  const [pendingReorder, setPendingReorder] = useState(false);
  const updateMutation = useUpdateAttributeFieldGroup();

  const displayed = useMemo(() => {
    if (!optimisticIds) return groups;
    const byId = new Map(groups.map((g) => [g.id, g]));
    return optimisticIds.map((id) => byId.get(id)).filter((g): g is AttributeFieldGroup => g !== undefined);
  }, [optimisticIds, groups]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const handleDragEnd = async (event: DragEndEvent): Promise<void> => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = displayed.findIndex((g) => g.id === active.id);
    const newIndex = displayed.findIndex((g) => g.id === over.id);
    if (oldIndex === -1 || newIndex === -1) return;

    const reordered = arrayMove(displayed, oldIndex, newIndex);
    setOptimisticIds(reordered.map((g) => g.id));
    setPendingReorder(true);

    const changed = reordered
      .map((group, index) => ({ group, nextSortOrder: index }))
      .filter(({ group, nextSortOrder }) => group.sort_order !== nextSortOrder);

    // Mirrors OptionsEditor.tsx's handleDragEnd. On failure (final-review
    // Finding D), only the OPTIMISTIC UI reverts — `optimisticIds` is
    // cleared and an explicit refetch re-renders from server truth, plus an
    // error toast fires — but any PATCH(es) that already committed before
    // the rejection stay persisted server-side (`Promise.all` cannot undo a
    // settled request). That can leave two groups sharing a `sort_order`
    // until the next successful reorder overwrites it (self-heals; never
    // corrupts data, just briefly unstable ordering).
    try {
      await Promise.all(
        changed.map(({ group, nextSortOrder }) =>
          updateMutation.mutateAsync({ id: group.id, body: { sort_order: nextSortOrder } }),
        ),
      );
    } catch {
      toast.error(t("pim.groups.reorderError"));
      await refetch();
    } finally {
      setOptimisticIds(null);
      setPendingReorder(false);
    }
  };

  const nextSortOrder = groups.length;

  return (
    <div className="flex flex-col gap-3" data-testid="pim-group-manager">
      {isLoading ? (
        <p className="text-sm text-[var(--g-secondary)]">{t("pim.groups.loading")}</p>
      ) : isError ? (
        <div className="flex items-center justify-between gap-2">
          <p className="text-sm text-[var(--g-error)]">{t("pim.groups.loadError")}</p>
          <Button type="button" variant="ghost" size="sm" onClick={() => void refetch()} className="min-h-[44px]">
            {t("pim.groups.retry")}
          </Button>
        </div>
      ) : displayed.length === 0 ? (
        <p className="text-sm text-[var(--g-secondary)]">{t("pim.groups.empty")}</p>
      ) : (
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={(e) => void handleDragEnd(e)}>
          <SortableContext items={displayed.map((g) => g.id)} strategy={verticalListSortingStrategy}>
            <ul className="flex flex-col gap-2" data-testid="pim-group-list">
              {displayed.map((group, index) => (
                <GroupRow key={group.id} group={group} index={index} dragDisabled={pendingReorder} />
              ))}
            </ul>
          </SortableContext>
        </DndContext>
      )}

      <AddGroupRow nextSortOrder={nextSortOrder} />
    </div>
  );
}

export default GroupManager;
